import { Directive, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';

/**
 * Drag-down-to-dismiss for Material bottom sheets — the standard phone-drawer
 * gesture, as an alternative to tapping the backdrop. Ported from homeflow's
 * `sheet-drag-dismiss.directive.ts` (the DESIGN.md §Sheets contract).
 *
 * Put it on a **grab handle** element at the top of the sheet (pair with the
 * global `.ng-sheet-grab` pill style), NOT on the whole sheet — the sheets'
 * bodies scroll, and a full-surface drag would fight the list.
 *
 *   <div class="ng-sheet-grab" ngSheetDrag aria-hidden="true"></div>
 *
 * The sheet follows the finger; releasing past the threshold dismisses (no
 * result, same as a backdrop tap), otherwise it springs back.
 *
 * A surface that only PRESENTS as a bottom sheet (the org-board side panel is
 * a `mat-drawer` re-anchored to the bottom edge on phones — see OE-162) can
 * borrow the identical gesture by naming its own container and handling the
 * dismissal itself:
 *
 *   <div class="ng-sheet-grab" ngSheetDrag
 *        ngSheetDragContainer="mat-drawer[position=end]"
 *        (ngSheetDragDismissed)="closePanel()"></div>
 */
@Directive({
    selector: '[ngSheetDrag]',
    standalone: true,
    host: {
        '(touchstart)': 'start($event)',
        '(touchmove)': 'move($event)',
        '(touchend)': 'end()',
        '(touchcancel)': 'end()',
    },
})
export class SheetDragDismissDirective {
    /** The element the drag transforms — defaults to the bottom-sheet container. */
    // eslint-disable-next-line @angular-eslint/no-input-rename -- public API consumed app-wide as ngSheetDragContainer (apps/newgenus-frontend/.../app.component.html); renaming would break every consumer
    @Input('ngSheetDragContainer') public containerSelector = 'mat-bottom-sheet-container';

    /**
     * Emitted instead of dismissing when there is no `MatBottomSheetRef` (a
     * borrowed surface owns its own close, and — unlike a sheet — survives it,
     * so the host must also clear the inline drag styles).
     */
    @Output('ngSheetDragDismissed') public dismissed = new EventEmitter<void>();

    private ref = inject(MatBottomSheetRef, { optional: true });
    private el = inject<ElementRef<HTMLElement>>(ElementRef);

    private startY: number | null = null;
    private dy = 0;

    private container(): HTMLElement | null {
        return this.el.nativeElement.closest(this.containerSelector);
    }

    /** Dismiss through whichever mechanism owns this surface. */
    private close(): void {
        if (this.ref) this.ref.dismiss();
        else this.dismissed.emit();
    }

    public start(e: TouchEvent): void {
        if (e.touches.length !== 1) {
            this.startY = null;
            return;
        }
        this.startY = e.touches[0].clientY;
        this.dy = 0;
        const c = this.container();
        if (c) c.style.transition = 'none';
    }

    public move(e: TouchEvent): void {
        if (this.startY === null) return;
        // Downward only — an upward drag clamps to the fully-open position.
        this.dy = Math.max(0, e.touches[0].clientY - this.startY);
        const c = this.container();
        if (c) c.style.transform = `translateY(${this.dy}px)`;
        // The handle owns this gesture — don't let the page scroll underneath.
        if (e.cancelable) e.preventDefault();
    }

    public end(): void {
        if (this.startY === null) return;
        this.startY = null;
        const c = this.container();
        if (this.dy > 90) {
            if (!c) {
                this.close();
                return;
            }
            // Finish the slide ourselves, hide, THEN dismiss. Dismissing while
            // still visible replays Material's exit animation from
            // translateY(0) — the sheet visibly pops back to fully-open and
            // slides down a second time (Angular animations don't start from
            // inline drag transforms).
            c.style.transition = 'transform 150ms ease-in';
            c.style.transform = 'translateY(110%)';
            window.setTimeout(() => {
                c.style.visibility = 'hidden';
                this.close();
            }, 160);
            return;
        }
        if (c) {
            c.style.transition = 'transform .18s ease';
            c.style.transform = '';
        }
    }
}
